import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useInView, animate, AnimatePresence } from 'motion/react';
import { Play, Pause, Volume2, VolumeX, Scan, Video, TrendingUp, Check, MapPin, Camera, Send, MousePointerClick, LayoutTemplate, ChevronRight, ArrowUpRight, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { NavLink } from 'react-router-dom';
import { Testimonials } from '../components/sections/Testimonials';
import { ClientLogos } from '../components/sections/ClientLogos';
import { EditorOverlay } from '../components/admin/EditorOverlay';
import { AppDownload } from '../components/sections/AppDownload';
import { Newsletter } from '../components/sections/Newsletter';
import { SEO } from '../components/ui/SEO';
import { useSiteContent } from '../hooks/useSiteContent';

import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';

interface PricingPackage {
  id: string;
  name: string;
  price: string;
  desc: string;
  features: string[];
  highlight: boolean;
  order: number;
}

const serviceAreas = [
  "Toronto", "Durham", "Markham", "York Region", "Northumberland", "Southern Kawarthas"
];

const clientLogosForHero = [
  "REVEL", "KELLER WILLIAMS", "SOTHEBY'S", "COMPASS", "THE AGENCY", "RE/MAX", "CHESTNUT PARK", "COHEN HOMES", "NEST SEEKERS", "ROYAL LEPAGE"
];

const AnimatedCounter = ({ value, label }: { value: number; label: string }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      const controls = animate(0, value, {
        duration: 3,
        onUpdate: (latest) => setDisplayValue(Math.round(latest))
      });
      return () => controls.stop();
    }
  }, [isInView, value]);

  return (
    <div ref={ref} className="text-center p-6 bg-zinc-900/30 border border-white/5 rounded-xl hover:border-brand-500/30 transition-colors">
      <h3 className="text-4xl md:text-5xl font-display font-bold text-white mb-2">
        {displayValue.toLocaleString()}{value > 5 ? '+' : '+' }
      </h3>
      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-300 font-bold">{label}</p>
    </div>
  );
};

const ReelVideo = ({ src, poster, onPopOut }: { src: string, poster?: string, onPopOut?: (src: string) => void }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoRef.current?.play().catch(() => {});
          setIsPlaying(true);
        } else {
          videoRef.current?.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`w-[300px] lg:w-[340px] aspect-[9/16] relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black flex-shrink-0 group ${onPopOut ? 'cursor-pointer' : ''}`}
      onClick={() => onPopOut && onPopOut(src)}
    >
      <video ref={videoRef} loop muted playsInline preload="metadata" className="w-full h-full object-cover" src={src} poster={poster} />
      <div className="absolute inset-0 border-4 border-black/20 rounded-2xl pointer-events-none z-20"></div>
      <div className="absolute bottom-4 left-4 z-30">
        <button 
          onClick={togglePlay} 
          className="w-10 h-10 bg-black/50 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white hover:text-black transition-all transform hover:scale-110"
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
        </button>
      </div>
      <div className="absolute bottom-4 right-4 z-30">
        <button 
          onClick={toggleMute} 
          className="w-10 h-10 bg-black/50 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white hover:text-black transition-all transform hover:scale-110"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      </div>
    </div>
  );
};

const BackgroundVideo = ({ src, poster, className }: { src: string, poster?: string, className?: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoRef.current?.play().catch(() => {});
        } else {
          videoRef.current?.pause();
        }
      },
      { threshold: 0.1 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, []);

  return (
    <video 
      ref={videoRef}
      src={src} 
      loop 
      muted 
      playsInline 
      preload="metadata"
      poster={poster} 
      className={className} 
    />
  );
};

const InteractiveVideo = ({ src, poster, aspectClass = "aspect-[9/16]", tag, onPopOut }: { src: string; poster?: string; aspectClass?: string; tag?: string; onPopOut?: (src: string) => void; }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          videoRef.current?.play().catch(() => {});
          setIsPlaying(true);
        } else {
          videoRef.current?.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`relative ${aspectClass} rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black group ${onPopOut ? 'cursor-pointer' : ''}`}
      onClick={() => onPopOut && onPopOut(src)}
    >
      <video ref={videoRef} loop muted playsInline preload="metadata" className="w-full h-full object-cover" src={src} poster={poster} />
      <div className="absolute inset-0 border-4 border-black/20 rounded-2xl pointer-events-none z-20"></div>
      {tag && (
        <div className="absolute top-6 left-6 z-30">
          <span className="inline-block px-5 py-2 md:px-6 md:py-2.5 text-[8px] md:text-[10px] font-display font-bold uppercase tracking-[0.25em] bg-[linear-gradient(to_bottom,rgba(0,0,0,0.4)_0%,rgba(0,0,0,0.1)_100%)] text-white rounded-[6px] backdrop-blur-xl shadow-lg border border-white/40 whitespace-nowrap">
            {tag}
          </span>
        </div>
      )}
      <div className="absolute bottom-6 left-6 z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <button 
          onClick={togglePlay} 
          className="w-12 h-12 bg-black/50 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white hover:text-black transition-all transform hover:scale-110"
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
        </button>
      </div>
      <div className="absolute bottom-6 right-6 z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <button 
          onClick={toggleMute} 
          className="w-12 h-12 bg-black/50 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center text-white hover:bg-white hover:text-black transition-all transform hover:scale-110"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
        </button>
      </div>
    </div>
  );
};

const ServiceAreaItem: React.FC<{ area: string }> = ({ area }) => (
  <motion.div initial="initial" whileHover="hover" className="py-6 flex flex-col items-center lg:items-end w-full cursor-pointer group">
    <motion.h3 variants={{ initial: { color: "#71717a", scale: 1 }, hover: { color: "#ffffff", scale: 1.15 } }} className="text-2xl md:text-4xl font-display font-bold uppercase tracking-tight">
      {area}
    </motion.h3>
    <div className="h-[2px] mt-4 w-[60px] bg-white/5 group-hover:bg-brand-500 group-hover:w-full transition-all duration-300" />
  </motion.div>
);

const staticPackages: PricingPackage[] = [
  {
    id: 'pkg-1',
    name: "Essential",
    price: "498",
    desc: "Perfect for condos and standard listings.",
    features: [
      "Interior and Exterior Photo Shoot",
      "Social Reel (45 to 60 seconds, level 1)",
      "Floor Plan with Dimensions",
      "Property Website (1 Year Hosting)"
    ],
    highlight: false,
    order: 1
  },
  {
    id: 'pkg-2',
    name: "Pro",
    price: "750",
    desc: "Our most popular package for agents.",
    features: [
      "Interior‑Exterior Photo Shoot",
      "Social Reel (45 to 60 seconds, level 1)",
      "Agent on camera",
      "Drone Shoot – photos plus video",
      "Floor Plan with Dimensions",
      "Property Website (1 Year Hosting)"
    ],
    highlight: true,
    order: 2
  },
  {
    id: 'pkg-3',
    name: "Ultimate",
    price: "1250",
    desc: "Complete production for luxury estates.",
    features: [
      "Interior and Exterior Photo Shoot",
      "Social Reel (45 to 60 seconds, level 1)",
      "Agent on camera",
      "Drone Photos and Video",
      "4K MLS Video Tour",
      "Floor Plan with Dimensions",
      "Property Website (1 Year Hosting)"
    ],
    highlight: false,
    order: 3
  }
];

const getProcessIcon = (iconName: string) => {
  switch (iconName) {
    case 'MousePointerClick': return <MousePointerClick size={18} />;
    case 'LayoutTemplate': return <LayoutTemplate size={18} />;
    case 'Camera': return <Camera size={18} />;
    case 'Send': return <Send size={18} />;
    default: return <ChevronRight size={18} />;
  }
};

export const Home: React.FC = () => {
  const [dynamicPackages, setDynamicPackages] = useState<PricingPackage[]>(staticPackages);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const getProcessIcon = (iconName: string | React.ReactNode) => {
    if (typeof iconName !== 'string') return iconName;
    
    switch (iconName) {
      case 'MousePointerClick': return <MousePointerClick size={18} />;
      case 'LayoutTemplate': return <LayoutTemplate size={18} />;
      case 'Camera': return <Camera size={18} />;
      case 'Send': return <Send size={18} />;
      default: return <ChevronRight size={18} />;
    }
  };

  const [dynamicServiceAreas, setDynamicServiceAreas] = useState<string[]>(serviceAreas);
  const [dynamicClientLogos, setDynamicClientLogos] = useState<string[]>(clientLogosForHero);
  const { content: siteContent } = useSiteContent('home');

  useEffect(() => {
    // Fetch dynamic client logos for hero
    const qLogos = query(collection(db, 'clientLogos'), orderBy('order', 'asc'));
    const unsubscribeLogos = onSnapshot(qLogos, (snapshot) => {
      if (!snapshot.empty) {
        const logosData = snapshot.docs.map(doc => doc.data().name) as string[];
        setDynamicClientLogos(logosData);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'clientLogos');
    });

    // Fetch dynamic packages
    const q = query(collection(db, 'packages'), orderBy('order', 'asc'));
    const unsubscribePackages = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const packagesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PricingPackage[];
        setDynamicPackages(packagesData);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'packages');
    });

    // Fetch dynamic service areas
    const qAreas = query(collection(db, 'serviceAreas'), orderBy('order', 'asc'));
    const unsubscribeAreas = onSnapshot(qAreas, (snapshot) => {
      if (!snapshot.empty) {
        const areasData = snapshot.docs.map(doc => doc.data().name) as string[];
        setDynamicServiceAreas(areasData);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'serviceAreas');
    });

    return () => {
      unsubscribePackages();
      unsubscribeAreas();
      unsubscribeLogos();
    };
  }, []);

  const processRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: processScrollProgress } = useScroll({
    target: processRef,
    offset: ["start 70%", "end 50%"]
  });

  return (
    <div className="bg-black text-white w-full overflow-x-hidden font-sans">
      <SEO title="Realta Media | Toronto Real Estate Media & Marketing" description="Realta Media is a real estate media and marketing partner in Toronto." />

      <div className="w-full bg-black">
        {/* WRAPPER FOR STICKY BACKGROUND SCOPE */}
        <div className="relative">
          {/* Localized Sticky Background Media (Limited to first 3 major sections) */}
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none bg-black">
            <div className="sticky top-0 h-[75vh] md:h-screen w-full">
              {/* Black Overlay on Video - Set to 20% for both mobile and desktop */}
              <div className="absolute inset-0 bg-black/20 z-10" />
              <video 
                autoPlay 
                loop 
                muted 
                playsInline 
                className="w-full h-full object-cover" 
                poster={siteContent.hero?.posterUrl || "https://realta.b-cdn.net/Media/Hero%20Section%20Fallback%20WO.jpg"}
                preload="metadata"
              >
                <source src={siteContent.hero?.videoUrl || "https://realta.b-cdn.net/Media/Hero%20Section%20Video%20WOv3.mp4"} type="video/mp4" />
              </video>
            </div>
          </div>

          {/* 1. HERO SECTION - Full height on mobile to ensure only hero is visible on load */}
          <EditorOverlay sectionId="hero" onSelect={() => {}}>
            <section className="relative h-screen min-h-[600px] w-full bg-transparent overflow-visible">
              {/* ... existing hero content ... */}
              <div 
                className="absolute inset-0 z-[1] md:hidden" 
                style={{ background: 'linear-gradient(to bottom, transparent 20%, rgba(0,0,0,0.1) 35%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.8) 65%, black 72%, black 100%)' }}
              ></div>

              {/* Desktop Gradients - Lightened to reveal more video but with a strong bottom fade */}
              <div className="hidden md:block absolute -top-[500px] left-0 right-0 -bottom-[1px] bg-gradient-to-t from-black via-transparent to-transparent z-[1]"></div>
              <div 
                className="hidden md:block absolute -top-[500px] left-0 right-0 -bottom-[1px] z-[1]" 
                style={{ background: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.4) 15%, rgba(0,0,0,0) 40%)' }}
              ></div>
              
              <div className="relative z-10 h-full flex flex-col items-center justify-center container mx-auto px-6 text-center">
                <div className="flex flex-col items-center translate-y-[23vh] md:translate-y-40">
                  <div className="flex flex-col items-center max-w-fit mx-auto">
                    <h1 className="text-[10vw] md:text-[5.6vw] lg:text-[4.23vw] font-display font-bold uppercase leading-[0.9] mb-4 tracking-[-0.02em] text-white drop-shadow-2xl inline-block whitespace-nowrap">
                      {siteContent.hero?.titleLine1 || "World Class Media"}<br/>
                      <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-brand-300 to-white bg-[length:200%_auto] animate-shine">
                        {siteContent.hero?.titleLine2 || "For Real Estate"}
                      </span>
                    </h1>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <NavLink to="/booking">
                       <button className="relative min-w-[154px] md:min-w-[174px] h-[43px] md:h-[44px] bg-brand-500 rounded-none overflow-hidden group transition-all duration-300 text-black">
                          <div className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                          <span className="relative z-10 font-display font-bold uppercase tracking-widest transition-colors duration-300 text-[11px] md:text-[0.7rem]">
                            {siteContent.hero?.buttonText || "Book Now"}
                          </span>
                       </button>
                    </NavLink>
                  </div>

                  {/* Mobile Logo Strip & Line Animation - Moved inside the main flex container for mobile to reduce gap */}
                  <div className="flex md:hidden flex-col items-center gap-4 w-full mt-5">
                      <div className="w-[85vw] overflow-hidden relative fade-mask">
                          <div className="flex animate-scroll whitespace-nowrap opacity-50 hover:opacity-100 transition-opacity duration-500 py-3 w-max">
                            {[...dynamicClientLogos, ...dynamicClientLogos].map((name, i) => (
                              <div key={i} className="inline-block mx-4">
                                <span className="text-[9px] font-display font-bold uppercase tracking-widest text-white">
                                  {name}
                                </span>
                              </div>
                            ))}
                          </div>
                      </div>
                      <div className="w-[1px] h-12 bg-white/10 relative overflow-hidden">
                          <motion.div 
                              className="absolute top-0 left-0 w-full h-1/3 bg-brand-500"
                              animate={{ top: ["-100%", "100%"] }}
                              transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                          />
                      </div>
                  </div>
                </div>
              </div>

              {/* Desktop Logo Strip & Line Animation - Hidden on mobile as it's now handled above */}
              <div className="hidden md:flex absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-4 w-full">
                  <div className="w-[60vw] max-w-[800px] overflow-hidden relative fade-mask">
                      <div className="flex animate-scroll whitespace-nowrap opacity-50 hover:opacity-100 transition-opacity duration-500 py-3 w-max">
                        {[...dynamicClientLogos, ...dynamicClientLogos].map((name, i) => (
                          <div key={i} className="inline-block mx-8">
                            <span className="text-[10px] font-display font-bold uppercase tracking-widest text-white">
                              {name}
                            </span>
                          </div>
                        ))}
                      </div>
                  </div>
                  <div className="w-[1px] h-16 bg-white/10 relative overflow-hidden">
                      <motion.div 
                          className="absolute top-0 left-0 w-full h-1/3 bg-brand-500"
                          animate={{ top: ["-100%", "100%"] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                      />
                  </div>
              </div>
            </section>
          </EditorOverlay>

          {/* 2. SERVICES GRID */}
          <EditorOverlay sectionId="services" onSelect={() => {}}>
            <section className="relative z-20 py-0 bg-dark-900/40 border-y border-white/5">
                <div className="grid grid-cols-1 md:grid-cols-3">
                  {[
                    { 
                      icon: <Scan size={32} />, 
                      title: siteContent.services?.photographyTitle || "Photography", 
                      desc: siteContent.services?.photographyDesc || "Magazine-quality photos that makes spaces feel bright, open, and inviting.", 
                      img: siteContent.services?.photographyImg || "https://realta.b-cdn.net/Media/WO%20LM-NLP-Hero%20Image.avif", 
                      link: "/listing-media", 
                      label: "Explore Listing Media" 
                    },
                    { 
                      icon: <Video size={32} />, 
                      title: siteContent.services?.videoTitle || "Cinematic Video", 
                      desc: siteContent.services?.videoDesc || "4K narratives that highlight the lifestyle and flow of a property, shot on cinema cameras.", 
                      img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2070&auto=format&fit=crop", 
                      video: siteContent.services?.videoUrl || "https://realta.b-cdn.net/Media/WO%201448%20Mannheim%20Rd%2C%20Wilmot%2C%20ON%20N0B%202H0.mp4", 
                      poster: siteContent.services?.videoPoster || "https://realta.b-cdn.net/Media/WO%20FAllback%20Image%201448%20Mannheim%20Rd%2C%20Wilmot%2C%20ON%20N0B%202H0%20(1).jpg", 
                      link: "/listing-media", 
                      label: "View Cinematic Video" 
                    },
                    { 
                      icon: <TrendingUp size={32} />, 
                      title: siteContent.services?.brandingTitle || "Branding", 
                      desc: siteContent.services?.brandingDesc || "Social-first content strategies to position you as the market expert and build your personal brand.", 
                      img: siteContent.services?.brandingImg || "https://realta.b-cdn.net/Media/WO%201x1%20Revel%20Office%20Presentation%20Vertical.jpg", 
                      link: "/brand-media", 
                      label: "Discover Brand Media" 
                    },
                  ].map((service, idx) => (
                    <div key={idx} className="group relative h-[450px] md:h-[550px] bg-black/80 overflow-hidden border-r border-white/5 last:border-r-0">
                       {service.video ? (
                          <BackgroundVideo src={service.video} poster={service.poster} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-40 group-hover:opacity-60" />
                       ) : (
                          <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110 opacity-40 group-hover:opacity-50" style={{backgroundImage: `url(${service.img})`}}></div>
                       )}
                       <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                       <div className="absolute bottom-0 left-0 p-10 w-full z-10 flex flex-col justify-end">
                          <div className="text-white mb-6 transition-transform duration-500 group-hover:-translate-y-2 opacity-80 group-hover:opacity-100">{service.icon}</div>
                          <h2 className="text-2xl md:text-3xl font-display font-bold uppercase mb-4 text-white group-hover:text-brand-500 transition-colors">{service.title}</h2>
                          <p className="text-zinc-300 text-sm leading-relaxed mb-8 max-w-xs transition-all duration-500 delay-100 font-light opacity-100 md:opacity-0 md:group-hover:opacity-100 md:transform md:translate-y-4 md:group-hover:translate-y-0">
                            {service.desc}
                          </p>
                          <NavLink to={service.link} className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-white hover:text-brand-500 transition-colors" aria-label={service.label}>{service.label} <ChevronRight size={12} /></NavLink>
                       </div>
                    </div>
                  ))}
                </div>
            </section>
          </EditorOverlay>

          {/* 3. LISTING REELS */}
          <EditorOverlay sectionId="reels" onSelect={() => {}}>
            <section className="relative z-20 py-20 md:py-32 bg-[#050505]/90 overflow-hidden border-b border-white/5 md:border-none">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[500px] bg-brand-500/5 rounded-full blur-[100px]"></div>
              <div className="w-full px-4 md:px-12 relative z-10">
                <div className="text-center mb-20">
                  <h2 className="text-4xl md:text-5xl font-display font-bold uppercase">{siteContent.reels?.title?.split(' ')[0] || "Listing"} <span className="text-zinc-400">{siteContent.reels?.title?.split(' ')[1] || "Reels"}</span></h2>
                </div>
                <div className="flex flex-col xl:flex-row justify-between items-center gap-8 lg:gap-8 w-full max-w-[1920px] mx-auto">
                  <div className="flex flex-col md:flex-row items-center gap-6 xl:w-[45%] xl:justify-end mb-16 xl:mb-0">
                    <h3 className="lg:hidden text-2xl font-display font-bold uppercase text-white tracking-wide text-center">{siteContent.reels?.level1Title || "Level 1 - Basic Walkthrough"}</h3>
                    <div className="hidden lg:flex w-[280px] h-[280px] relative flex-shrink-0 flex-col justify-center group [perspective:1000px]">
                      <div className="relative w-full h-full transition-all duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)] cursor-pointer">
                        <div className="absolute inset-0 [backface-visibility:hidden]">
                          <div className="relative w-full h-full rounded-xl overflow-hidden border border-white/5 bg-white/5 backdrop-blur-sm flex flex-col justify-center text-center p-8">
                            <div className="mb-6"><span className="inline-block px-2 py-1 rounded bg-brand-500/10 text-brand-500 text-[9px] font-bold uppercase tracking-widest border border-brand-500/20">Level 1</span></div>
                            <h3 
                              className="text-xl font-display font-bold uppercase text-white tracking-wide mb-2"
                            >
                              {siteContent.reels?.level1Title?.replace('Level 1 - ', '') || "Basic Walkthrough"}
                            </h3>
                          </div>
                        </div>
                        <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)]">
                          <div className="relative w-full h-full rounded-xl overflow-hidden border border-brand-500/30 bg-zinc-900 flex flex-col justify-center text-center p-8">
                            <p className="text-zinc-300 text-[10px] leading-relaxed mb-6 font-light uppercase tracking-widest">{siteContent.reels?.level1Desc || "Quick tour for social media. 30-45s vertical cut."}</p>
                            <NavLink to="/booking"><Button size="sm" variant="ghost" className="w-full text-[9px] border border-white/20 hover:border-brand-500 h-8 px-2">Book Now</Button></NavLink>
                          </div>
                        </div>
                      </div>
                    </div>
                    <ReelVideo src={siteContent.reels?.level1Video || "https://realta.b-cdn.net/Media/WO%20Level%201%20Doug%20Gordin%20Whitby%2C%20Ontariov2.mp4"} poster={siteContent.reels?.level1Poster || "https://realta.b-cdn.net/Media/WO%20Leve%201%20-%20Example%201%20Fallback%20(1).jpg"} onPopOut={setSelectedVideo} />
                  </div>
                  <div className="hidden xl:block w-[1px] h-80 bg-white/30 blur-[1px]"></div>
                  <div className="flex flex-col-reverse md:flex-row items-center gap-6 xl:w-[45%] xl:justify-start">
                    <ReelVideo src={siteContent.reels?.level2Video || "https://realta.b-cdn.net/Media/WO%20404%20Cox%20Mill%20v3a_prob4.mp4"} poster={siteContent.reels?.level2Poster || "https://realta.b-cdn.net/Media/WO%20404%20Cox%20Mill%20v3a%20fallback.jpg"} onPopOut={setSelectedVideo} />
                    <div className="flex flex-col items-center">
                      <h3 className="lg:hidden text-2xl font-display font-bold uppercase text-white tracking-wide text-center mb-4">{siteContent.reels?.level2Title || "Level 2 - Advanced Visuals"}</h3>
                      <div className="hidden lg:flex w-[280px] h-[280px] relative flex-shrink-0 flex-col justify-center group [perspective:1000px]">
                        <div className="relative w-full h-full transition-all duration-500 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)] cursor-pointer">
                          <div className="absolute inset-0 [backface-visibility:hidden]">
                            <div className="relative w-full h-full rounded-xl overflow-hidden border border-white/5 bg-white/5 backdrop-blur-sm flex flex-col justify-center text-center p-8">
                              <div className="mb-6"><span className="inline-block px-2 py-1 rounded bg-brand-500/10 text-brand-500 text-[9px] font-bold uppercase tracking-widest border border-brand-500/20">Level 2</span></div>
                              <h3 
                                className="text-xl font-display font-bold uppercase text-white tracking-wide mb-2"
                              >
                                {siteContent.reels?.level2Title?.replace('Level 2 - ', '') || "Advanced Visuals"}
                              </h3>
                            </div>
                          </div>
                          <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)]">
                            <div className="relative w-full h-full rounded-xl overflow-hidden border border-brand-500/30 bg-zinc-900 flex flex-col justify-center text-center p-8">
                              <p className="text-zinc-300 text-[10px] leading-relaxed mb-6 font-light uppercase tracking-widest">{siteContent.reels?.level2Desc || "High-end transitions & speed ramps. The ultimate scroll-stopper."}</p>
                              <NavLink to="/booking"><Button size="sm" variant="ghost" className="w-full text-[9px] border border-white/20 hover:border-brand-500 h-8 px-2">Book Now</Button></NavLink>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </EditorOverlay>
        </div>

        {/* REST OF HOME SECTIONS */}
        <EditorOverlay sectionId="differentiate" onSelect={() => {}}>
          <section className="relative z-20 py-20 md:py-32 bg-black border-t border-white/5 border-b md:border-b-0">
             <div className="container mx-auto px-6 max-w-[1600px]">
                <div className="mb-16 text-center">
                  <h2 className="text-4xl md:text-6xl font-display font-bold uppercase leading-none mb-6">
                    {siteContent.differentiate?.title?.split('. ')[0] || "Differentiate From Competitors."}.<br/>
                    <span className="text-zinc-400">{siteContent.differentiate?.title?.split('. ')[1] || "Build Trust."}</span>
                  </h2>
                </div>
                <div className="flex flex-col lg:flex-row gap-8 lg:h-[600px] mb-8 items-stretch">
                   <div className="w-full lg:w-auto lg:h-full aspect-[9/16]">
                      <InteractiveVideo 
                        src={siteContent.differentiate?.video1Url || "https://realta.b-cdn.net/Media/WO%20Winvillle%20Road%2C%20Pickering%20ON_prob4v2.mp4"} 
                        poster={siteContent.differentiate?.video1Poster || "https://realta.b-cdn.net/Media/WO%20Winvillle%20Road%2C%20Pickering%20ON-Fallback%20(1).jpg"} 
                        aspectClass="w-full h-full" 
                        tag={siteContent.differentiate?.video1Tag || "Level 2 Reel with Twilight add-on"} 
                        onPopOut={setSelectedVideo}
                      />
                   </div>
                   <div className="w-full lg:h-full flex-grow aspect-video">
                      <InteractiveVideo 
                        src={siteContent.differentiate?.video2Url || "https://realta.b-cdn.net/Media/WO%20Bar%20Bc%20Ranch%20Rd.mp4"} 
                        poster={siteContent.differentiate?.video2Poster || "https://realta.b-cdn.net/Media/WO%20Bar%20Bc%20Ranch%20Rd%20Fallback%20(1).jpg"} 
                        aspectClass="w-full h-full" 
                        tag={siteContent.differentiate?.video2Tag || "Custom Cinematic Tour"} 
                        onPopOut={setSelectedVideo}
                      />
                   </div>
                </div>
                <div className="w-full pt-8 border-t border-white/5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                         <AnimatedCounter value={siteContent.differentiate?.counter1Value || 2000} label={siteContent.differentiate?.counter1Label || "Properties Filmed"} />
                         <AnimatedCounter value={siteContent.differentiate?.counter2Value || 300} label={siteContent.differentiate?.counter2Label || "Happy Agents"} />
                         <AnimatedCounter value={siteContent.differentiate?.counter3Value || 1} label={siteContent.differentiate?.counter3Label || "Billion Sold ($) +"} />
                         <AnimatedCounter value={siteContent.differentiate?.counter4Value || 5} label={siteContent.differentiate?.counter4Label || "Years Active"} />
                    </div>
                </div>
             </div>
          </section>
        </EditorOverlay>

        <EditorOverlay sectionId="packages" onSelect={() => {}}>
          <section className="relative z-20 py-20 md:py-32 bg-black border-b border-white/5 md:border-none">
             <div className="container mx-auto px-6">
                <div className="text-center mb-20">
                   <span className="text-brand-500 font-bold uppercase tracking-[0.2em] text-[10px]">Investment</span>
                   <h2 className="text-4xl md:text-5xl font-display font-bold uppercase mt-4 text-white">Listing <span className="text-zinc-400">Packages</span></h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1600px] mx-auto mb-12">
                   {dynamicPackages.map((pkg, i) => (
                      <div 
                        key={pkg.id} 
                        className={`relative flex flex-col p-10 transition-all duration-500 border rounded-sm group ${
                          pkg.highlight 
                          ? 'bg-white/5 border-brand-500/30 transform md:-translate-y-4 z-10 backdrop-blur-sm shadow-[0_0_30px_rgba(54,180,253,0.1)] hover:shadow-[0_0_50px_rgba(54,180,253,0.3)] hover:scale-105 hover:md:-translate-y-8' 
                          : 'bg-zinc-900/40 border-white/10 hover:bg-zinc-900/60 hover:border-white/20 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] hover:scale-105 hover:-translate-y-4'
                        }`}
                      >
                         {pkg.highlight && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-brand-500 text-white px-6 py-1.5 text-[10px] font-bold uppercase tracking-widest shadow-[0_0_20px_rgba(54,180,253,0.4)]">Most Popular</div>}
                         <div className="mb-8"><h3 className="text-2xl font-display font-bold uppercase mb-2 text-white">{pkg.name}</h3><p className="text-zinc-300 text-xs min-h-[20px]">{pkg.desc}</p></div>
                         <div className="text-5xl font-display font-bold mb-8 text-white tracking-tight">${pkg.price}</div>
                         <div className="flex-grow">
                            <ul className="space-y-5 mb-12">
                              {pkg.features.map((feat, idx) => (
                                <li key={idx} className="flex gap-4 items-start text-sm text-zinc-300">
                                  <Check size={14} className="text-brand-500 mt-1 flex-shrink-0" />
                                  <span className="uppercase tracking-wide text-[11px] font-medium leading-relaxed">{feat}</span>
                                </li>
                              ))}
                            </ul>
                         </div>
                         <NavLink to="/booking" className="w-full">
                           <button className={`w-full py-4 text-[10px] font-bold uppercase tracking-widest border transition-all duration-300 ${
                             pkg.highlight 
                             ? 'bg-white text-black border-white hover:bg-brand-500 hover:border-brand-500 hover:text-black' 
                             : 'border-white/20 text-white hover:bg-white hover:text-black hover:border-white'
                           }`}>
                             Select Package
                           </button>
                         </NavLink>
                      </div>
                   ))}
                   {dynamicPackages.length === 0 && (
                     <div className="col-span-full py-20 text-center text-zinc-500 italic">
                       No packages found.
                     </div>
                   )}
                </div>

                <div className="text-center w-full max-w-[1600px] mx-auto">
                    <div className="border-t border-white/10 pt-6 mt-6 md:mt-8 mx-auto w-full md:w-2/3 lg:w-1/2">
                        <p className="text-zinc-400 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-3">
                            <span className="hidden md:block w-8 h-[1px] bg-brand-500/50"></span>
                            Add-ons available for upgrading the video
                            <span className="hidden md:block w-8 h-[1px] bg-brand-500/50"></span>
                        </p>
                    </div>
                </div>
             </div>
          </section>
        </EditorOverlay>

        <EditorOverlay sectionId="process" onSelect={() => {}}>
          <section className="relative z-20 py-20 md:py-40 bg-black overflow-hidden border-b border-white/5 md:border-none">
            <div className="container mx-auto px-6 relative">
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                  <div className="flex flex-col justify-center">
                      <h2 className="text-6xl md:text-8xl font-display font-bold uppercase mb-8 leading-[0.9] tracking-tighter pr-4 lg:pr-12">
                        {siteContent.process?.title?.split(' ')[0] || "Our"} <br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-500 to-white">{siteContent.process?.title?.split(' ')[1] || "Process"}</span>
                      </h2>
                      <p className="text-zinc-300 text-lg leading-relaxed mb-12 max-w-lg font-light">
                        {siteContent.process?.description || "You need story-first media to captivate your audience, draw in buyers and get your listing sold. Schedule a discovery call today."}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-6">
                          <NavLink to="/booking">
                            <Button className="bg-white text-black border-white hover:bg-brand-500 hover:text-black shadow-[0_0_30px_rgba(255,255,255,0.2)]" aria-label="Book Now To Start Process">
                              Book Now
                            </Button>
                          </NavLink>
                          <a href="https://cal.realta.media/discoverycall" target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" aria-label="Book Free Discovery Call">
                              Book Free Discovery Call
                            </Button>
                          </a>
                      </div>
                  </div>
                  <div className="relative pl-10" ref={processRef}>
                      <div className="absolute left-[19px] top-4 bottom-4 w-[2px] bg-white/10 rounded-full"></div>
                      <motion.div className="absolute left-[19px] top-4 w-[2px] bg-brand-500 origin-top rounded-full" style={{ height: "calc(100% - 32px)", scaleY: processScrollProgress }} />
                      <div className="space-y-12">
                        {(siteContent.process?.steps?.length > 0 && siteContent.process.steps.some((s: any) => s.title) ? siteContent.process.steps : [
                          { step: "Step 1", title: "Book Your Shoot", desc: "Schedule your shoot online or by phone instantly.", icon: "MousePointerClick" },
                          { step: "Step 2", title: "Select Package", desc: "Choose the services and add-ons you need.", icon: "LayoutTemplate" },
                          { step: "Step 3", title: "Shoot Day", desc: "Our team arrives to capture your listing perfectly.", icon: "Camera" },
                          { step: "Step 4", title: "Media Delivery", desc: "Receive your edited media in 1-2 business days.", icon: "Send" },
                        ]).map((item: any, i: number) => (
                          <div key={i} className="relative pl-12">
                             <div className="absolute left-[12px] top-1/2 -translate-y-1/2 w-4 h-4 bg-black border-2 border-brand-500 rounded-full z-10 shadow-[0_0_10px_rgba(54,180,253,0.5)]"><div className="w-full h-full bg-brand-500 rounded-full animate-ping opacity-20" /></div>
                             <div className="bg-zinc-900/40 border border-white/10 p-8 rounded-2xl hover:bg-zinc-900/80 hover:border-brand-500/50 transition-all duration-300 group hover:-translate-x-1 shadow-lg cursor-default">
                                <div className="flex items-center gap-3 mb-4"><span className="text-[10px] font-bold uppercase tracking-widest text-brand-500">{item.step}</span><div className="text-brand-500 group-hover:text-white transition-colors">{getProcessIcon(item.icon)}</div></div>
                                <h3 className="text-2xl font-display font-bold uppercase mb-2 text-white">{item.title}</h3>
                                <p className="text-zinc-300 text-sm leading-relaxed">{item.desc}</p>
                             </div>
                          </div>
                        ))}
                      </div>
                  </div>
               </div>
            </div>
          </section>
        </EditorOverlay>

        <div className="relative z-20"><Testimonials /></div>
        <div className="relative z-20"><ClientLogos /></div>

        <EditorOverlay sectionId="ready" onSelect={() => {}}>
          <section className="relative z-20 pt-24 pb-12 md:pt-40 md:pb-20 bg-black overflow-hidden border-t border-white/5">
            <div className="container mx-auto px-6 relative z-10">
               <div className="flex flex-col lg:flex-row items-center gap-24">
                  <div className="w-full lg:w-1/2 text-center lg:text-left flex flex-col items-center lg:items-start">
                      <div className="max-w-fit">
                          <h2 className="text-6xl md:text-8xl font-display font-bold uppercase text-white leading-none mb-10 tracking-tighter">
                            {siteContent.ready?.title?.split(' ')[0] || "Ready"} {siteContent.ready?.title?.split(' ')[1] || "To"} <br/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-500 to-white">{siteContent.ready?.title?.split(' ')[2] || "Dominate?"}</span>
                          </h2>
                          <p className="text-zinc-300 text-lg mb-12 font-light max-w-full lg:max-w-[90%]">
                            {siteContent.ready?.description || "Schedule your shoot today and receive your photos the next morning."}
                          </p>
                          <div className="flex flex-col sm:flex-row gap-6 w-full md:w-auto">
                              <NavLink to="/booking" className="inline-block w-full">
                                <Button size="lg" className="bg-white text-black border-white hover:bg-brand-500 hover:text-black w-full h-20 text-sm px-16 shadow-[0_0_30px_rgba(255,255,255,0.2)]">
                                  {siteContent.ready?.buttonText || "Book Your Shoot"}
                                </Button>
                              </NavLink>
                              <a href="https://cal.realta.media/discoverycall" target="_blank" rel="noopener noreferrer" className="w-full">
                                <Button size="lg" variant="outline" className="w-full h-20 text-sm">Discovery Call</Button>
                              </a>
                          </div>
                      </div>
                  </div>
                  <div className="w-full lg:w-1/2">
                     <div className="flex flex-col gap-6 items-center text-center lg:items-end lg:text-right">
                        <div className="flex items-center gap-3 mb-10 text-brand-500"><MapPin size={24} /><span className="font-display font-bold uppercase tracking-widest text-sm">Service Areas</span></div>
                        <div className="flex flex-col w-full">{dynamicServiceAreas.map((area, index) => (<ServiceAreaItem key={index} area={area} />))}</div>
                     </div>
                  </div>
               </div>
            </div>
          </section>
        </EditorOverlay>
        
        <EditorOverlay sectionId="appDownload" onSelect={() => {}}>
          <AppDownload content={siteContent.appDownload} />
        </EditorOverlay>
        <Newsletter />
      </div>

      <AnimatePresence>
        {selectedVideo && (
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-xl p-4 md:p-10"
                onClick={() => setSelectedVideo(null)}
            >
                <button 
                    onClick={() => setSelectedVideo(null)}
                    className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-brand-500 hover:text-black rounded-full text-white transition-all z-50"
                >
                    <X size={24} />
                </button>
                <div className="relative w-full max-w-5xl aspect-[9/16] md:aspect-video rounded-2xl overflow-hidden shadow-2xl border border-white/10" onClick={(e) => e.stopPropagation()}>
                    <video 
                        src={selectedVideo}
                        autoPlay
                        controls
                        className="w-full h-full object-contain bg-black"
                    />
                </div>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};